import useResizeObserver from "@react-hook/resize-observer";
import clsx from "clsx";
import { motion } from "motion/react";
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";

const COLLAPSED_HEIGHT = 88;

type Props = PropsWithChildren<{
  openLabel?: string;
  closeLabel?: string;
}>;

export const ExpandableSection = ({
  children,
  openLabel,
  closeLabel,
}: Props) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);

  // Check if content is expandable by comparing scrollHeight with collapsed height
  // Toggle button should not be rendered if content height is less than or equal to collapsed height
  const checkExpandable = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const hasOverflow = container.scrollHeight > COLLAPSED_HEIGHT;
    setIsExpandable(hasOverflow);

    if (!hasOverflow) {
      setIsExpanded(false);
    }
  }, []);

  useResizeObserver(containerRef, checkExpandable);

  useEffect(() => {
    checkExpandable();
  }, [children, checkExpandable]);

  return (
    <>
      <motion.div
        ref={containerRef}
        initial={false}
        animate={{
          height: isExpandable && !isExpanded ? COLLAPSED_HEIGHT : "auto",
        }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className="relative mb-2 overflow-hidden"
      >
        {children}
        {isExpandable && (
          <div
            className={clsx(
              "absolute bottom-0 h-15 w-full bg-linear-to-b from-white/0",
              {
                "to-white/100": !isExpanded,
                "to-white/0": isExpanded,
              },
            )}
          />
        )}
      </motion.div>

      {isExpandable && (
        <Button
          variant="text"
          onClick={(e) => {
            setIsExpanded((prev) => !prev);
            e.currentTarget.blur();
          }}
          fullWidth
        >
          {isExpanded
            ? (closeLabel ??
              intl.formatMessage({
                defaultMessage: "See less",
                description:
                  "Toggle label to collapse an expanded content section",
              }))
            : (openLabel ??
              intl.formatMessage({
                defaultMessage: "See more",
                description:
                  "Toggle label to expand a collapsed content section",
              }))}
        </Button>
      )}
    </>
  );
};
